/* eslint-disable relay/unused-fields */
import { type FunctionComponent, type ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { batchQueryAbWidgetQuery } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";

import { reorderEdgesByEntityIds } from "./reorder-edges";

type Props = {
  cftEntityIds: string[];
  cftTopicEntityId?: string;
  children: (data: batchQueryAbWidgetQuery["response"]) => ReactNode;
  openQuestionEntityIds: string[];
  openQuestionTopicEntityId?: string;
  seriesEntityIds: string[];
  seriesTopicEntityId?: string;
  spotlightEntityIds: string[];
};

export const AbWidgetBatchQuery: FunctionComponent<Props> = ({
  cftEntityIds,
  cftTopicEntityId,
  children,
  openQuestionEntityIds,
  openQuestionTopicEntityId,
  seriesEntityIds,
  seriesTopicEntityId,
  spotlightEntityIds,
}) => {
  const data = useLazyLoadQuery<batchQueryAbWidgetQuery>(
    graphql`
      query batchQueryAbWidgetQuery(
        $seriesEntityIds: [String!]!
        $seriesFirst: Int!
        $seriesTopicEntityId: String
        $withSeriesTopic: Boolean!
        $openQuestionEntityIds: [String!]!
        $openQuestionFirst: Int!
        $openQuestionTopicEntityId: String
        $withOpenQuestionTopic: Boolean!
        $cftEntityIds: [String!]!
        $cftFirst: Int!
        $cftTopicEntityId: String
        $withCftTopic: Boolean!
        $spotlightEntityIds: [String!]!
        $spotlightFirst: Int!
      ) {
        widget1: articles(filter: { entityIds: $seriesEntityIds }, first: $seriesFirst) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withLandscape250x99: true, withSize540x360: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
        seriesTopic: topic(filter: { entityId: $seriesTopicEntityId })
          @include(if: $withSeriesTopic) {
          ...entityLink
        }
        widget2: articles(
          filter: { entityIds: $openQuestionEntityIds }
          first: $openQuestionFirst
        ) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withSize540x360: true, withSize1200x800: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
        openQuestionTopic: topic(filter: { entityId: $openQuestionTopicEntityId })
          @include(if: $withOpenQuestionTopic) {
          ...entityLink
        }
        widget3: articles(filter: { entityIds: $cftEntityIds }, first: $cftFirst) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withSize1200x800: true, withSize540x360: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
        cftTopic: topic(filter: { entityId: $cftTopicEntityId }) @include(if: $withCftTopic) {
          ...entityLink
        }
        widget4: articles(filter: { entityIds: $spotlightEntityIds }, first: $spotlightFirst) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withSize1200x800: true, withSize540x360: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
      }
    `,
    {
      cftEntityIds,
      cftFirst: cftEntityIds.length,
      cftTopicEntityId: cftTopicEntityId ?? "",
      openQuestionEntityIds,
      openQuestionFirst: openQuestionEntityIds.length,
      openQuestionTopicEntityId: openQuestionTopicEntityId ?? "",
      seriesEntityIds,
      seriesFirst: seriesEntityIds.length,
      seriesTopicEntityId: seriesTopicEntityId ?? "",
      spotlightEntityIds,
      spotlightFirst: spotlightEntityIds.length,
      withCftTopic: !!cftTopicEntityId,
      withOpenQuestionTopic: !!openQuestionTopicEntityId,
      withSeriesTopic: !!seriesTopicEntityId,
    },
    { fetchPolicy: "network-only" },
  );

  const reorderedData = {
    ...data,
    widget1: {
      ...data.widget1,
      edges: reorderEdgesByEntityIds(data.widget1?.edges, seriesEntityIds),
    },
    widget2: {
      ...data.widget2,
      edges: reorderEdgesByEntityIds(data.widget2?.edges, openQuestionEntityIds),
    },
    widget3: {
      ...data.widget3,
      edges: reorderEdgesByEntityIds(data.widget3?.edges, cftEntityIds),
    },
    widget4: {
      ...data.widget4,
      edges: reorderEdgesByEntityIds(data.widget4?.edges, spotlightEntityIds),
    },
  };

  return children(reorderedData);
};
